import { debounce } from '@mui/material'
import { useAppSelector } from 'app/state'
import { store } from 'app/state/store'
import { queryClient, queryClientUpdateCachedValue, queryClientUpdateExistingCachedArrayElement } from 'queryClient'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import {
  connectionQuery,
  connectionsQuery,
  connectionsQueryKeys,
  getBqProjectsQuery,
  getBqProjectsWithConnectionQuery,
  getConnectionRelatedMapsQuery,
  getConnectionRelatedWorkflowsQuery,
  getDatabricksMetadataQuery
} from '../api/connectionsQueries'

import {
  Connection,
  DatabricksMetadataPayload,
  DatabricksRestParams,
  DatabricksWarehouseParams
} from 'features/connections/types'

import { refreshDatasetsByConnections } from 'features/builder/state/builderSlice'
import { CartoApiError } from 'features/common'
import { UseFormReturn } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import settings from 'settings'
import {
  attachMultipleConnectionsCredentials,
  getRecentResources,
  patchConnection,
  removeMultipleConnectionCredentials,
  saveUserOwnerConnections,
  deleteConnectionWithTransfer,
  deleteConnection
} from '../api/connections'
import { setBigqueryConnectionCreatorProjectsError, updateConnectionFromList } from '../state/connectionsSlice'
import { BigQueryConnectionProject } from '../types'
import useOAuth from '../ui/ConnectionForm/DBConnectionForm/useOAuth'
import { useMeUser } from 'features/users/hooks/useMeUser'
import { getRoleInfo } from 'features/users/model/user'
import { UserRole } from 'features/users'
import useAccountQueries from 'features/account/api/accountQueries'

export function useConnections({ refetchOnWindowFocus = true }: { refetchOnWindowFocus?: boolean } = {}) {
  const user = useMeUser()
  const isGuest = getRoleInfo(user).role === UserRole.Guest
  return useQuery({ ...connectionsQuery, enabled: !isGuest, refetchOnWindowFocus })
}

export function useConnectionQuery(id: string | undefined) {
  return useQuery(connectionQuery(id))
}

export function useConnection(id = '') {
  const data = queryClient.getQueryData<Connection>(connectionsQueryKeys.connection(id))

  return { data }
}

export const useRecentResources = (pageSize?: number) => {
  return useQuery({
    queryKey: connectionsQueryKeys.recentResources,
    queryFn: () => getRecentResources(pageSize)
  })
}

export function useSaveMultipleConnectionCredentialsMutation(connections: Connection[], userId: string | undefined) {
  const dispatch = useDispatch()

  const allUserOwnerConnections = connections.every((connection) => connection && userId === connection?.user_id)

  // Mutation fx depending if the user owns connection then update connection otherwise attach credentials
  const mutationFn = allUserOwnerConnections
    ? (connectionsWithCredentials) => {
        const mappedConnections = connectionsWithCredentials.map((conn) => {
          const matchingConnection = connections.find((c) => c.id === conn.connectionId) as Connection & {
            config: {
              credentials: {
                account: string
              }
            }
          }

          return {
            ...conn,
            id: conn.connectionId,
            config: {
              ...conn.config,
              credentials: {
                ...conn.config.credentials,
                database: matchingConnection?.config?.credentials?.database,
                account: matchingConnection?.config?.credentials?.account
              }
            }
          }
        })
        return saveUserOwnerConnections(mappedConnections)
      }
    : attachMultipleConnectionsCredentials

  return useMutation({
    mutationFn,
    onSuccess: (data) => {
      data.forEach(({ connectionId: id, error }) => {
        if (error) return

        const updates = { id, viewerCredentialsAttached: true }
        dispatch(updateConnectionFromList(updates))
        queryClientUpdateExistingCachedArrayElement(connectionsQueryKeys.connections, updates)
        queryClientUpdateCachedValue(connectionsQueryKeys.connection(id), updates)
      })
      queryClient.invalidateQueries(connectionsQueryKeys.connections)
    }
  })
}

export function useDeleteMultipleConnectionCredentialsMutation() {
  const dispatch = useDispatch()
  const { resetOAuthStatus } = useOAuth()

  return useMutation({
    mutationFn: removeMultipleConnectionCredentials,
    onSuccess: (data) => {
      const connectionsWithRemovedCredentials = data.filter((e) => !e.error)
      for (const { connectionId: id } of connectionsWithRemovedCredentials) {
        dispatch(updateConnectionFromList({ id, viewerCredentialsAttached: false }))
        queryClientUpdateExistingCachedArrayElement(connectionsQueryKeys.connections, {
          id,
          viewerCredentialsAttached: false
        })
        resetOAuthStatus()
        queryClientUpdateCachedValue(connectionsQueryKeys.connection(id), { viewerCredentialsAttached: false })
      }
      queryClient.invalidateQueries(connectionsQueryKeys.connections)
      dispatch(refreshDatasetsByConnections(connectionsWithRemovedCredentials.map(({ connectionId }) => connectionId)))
    }
  })
}

export function usePatchConnectionMutation() {
  const dispatch = useDispatch()
  return useMutation({
    mutationFn: patchConnection,
    onSuccess: (data) => {
      dispatch(updateConnectionFromList(data))
      queryClientUpdateExistingCachedArrayElement(connectionsQueryKeys.connections, { groups: [], ...data })
      queryClientUpdateCachedValue(connectionsQueryKeys.connection(data.id), { groups: [], ...data })
    }
  })
}

export function useDeleteConnectionMutation() {
  return useMutation({
    mutationFn: (id: string) => deleteConnection(id),
    onSuccess: () => {
      queryClient.invalidateQueries(connectionsQueryKeys.all)
    }
  })
}

export function useDeleteConnectionWithTransferMutation() {
  return useMutation({
    mutationFn: ({
      sourceConnectionId,
      targetConnectionId
    }: {
      sourceConnectionId: string
      targetConnectionId: string
    }) => deleteConnectionWithTransfer(sourceConnectionId, targetConnectionId),
    onSuccess: () => {
      queryClient.invalidateQueries(connectionsQueryKeys.all)
    }
  })
}

export function useConnectionActionsAllowed(connection?: Connection) {
  const user_id = useAppSelector((state) => state.users.me?.user_id)

  if (!connection) return {}

  const isEditable = connection.editable // There are read-only connections, like CartoDW or BigQueryConfigType.WorkloadIdentity

  const userIsTheOwner = connection.user_id === user_id
  const canEdit = userIsTheOwner && isEditable
  const canDelete = canEdit

  return {
    canEdit,
    canDelete
  }
}

type BqBillingProjectsProps = {
  connectionId?: string
  serviceAccount?: string
  wif?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  oauthState?: any
  search?: string
  serviceAccountEmail?: string
}

export function useBqBillingProjects({
  connectionId,
  oauthState,
  serviceAccount,
  serviceAccountEmail,
  wif,
  search
}: BqBillingProjectsProps) {
  const [isLoading, setLoading] = useState<boolean>(false)
  const [data, setData] = useState<BigQueryConnectionProject[] | undefined>()

  const getProjectsWithConnection = useCallback(
    async (search) => {
      const data = await queryClient.fetchQuery(getBqProjectsWithConnectionQuery(connectionId, search))
      return data
    },
    [connectionId]
  )

  const getProjectWithOAuth = useCallback(
    async (search) => {
      try {
        return await queryClient.fetchQuery(
          getBqProjectsQuery({ client_email: oauthState?.email, state: oauthState?.state }, search)
        )
      } catch (e) {
        store.dispatch(setBigqueryConnectionCreatorProjectsError(e as CartoApiError))
      }
    },
    [oauthState]
  )

  const getProjectWithServiceAccount = useCallback(
    async (search) => {
      let data = []
      try {
        data = await queryClient.fetchQuery(getBqProjectsQuery(JSON.parse(serviceAccount as string), search))
      } catch (e) {
        store.dispatch(setBigqueryConnectionCreatorProjectsError(e as CartoApiError))
      }
      return data
    },
    [serviceAccount]
  )

  const getProjectWithWif = useCallback(
    async (search) => {
      let data = []
      try {
        data = await queryClient.fetchQuery(
          getBqProjectsQuery({ type: 'workload_identity', serviceAccountEmail }, search)
        )
        store.dispatch(setBigqueryConnectionCreatorProjectsError(undefined))
      } catch (e) {
        store.dispatch(setBigqueryConnectionCreatorProjectsError(e as CartoApiError))
      }
      return data
    },
    [serviceAccountEmail]
  )

  const getProjectsBasedOnConnection = useCallback(
    async (search) => {
      if (connectionId && !wif) {
        return await getProjectsWithConnection(search)
      } else if (oauthState) {
        return await getProjectWithOAuth(search)
      } else if (serviceAccount) {
        return await getProjectWithServiceAccount(search)
      } else if (wif) {
        return await getProjectWithWif(search)
      } else {
        return []
      }
    },
    [
      connectionId,
      getProjectsWithConnection,
      getProjectWithOAuth,
      getProjectWithServiceAccount,
      getProjectWithWif,
      oauthState,
      serviceAccount,
      wif
    ]
  )

  const getProjects = useCallback(
    async (search) => {
      setLoading(true)

      try {
        const data = await getProjectsBasedOnConnection(search)
        setData(data)
      } catch (e) {
        // Do nothing
      } finally {
        setLoading(false)
      }
    },
    [getProjectsBasedOnConnection]
  )

  const getProjectsDebounced = useMemo(() => debounce(getProjects, 500), [getProjects])

  useEffect(() => {
    getProjectsDebounced(search)
  }, [getProjectsDebounced, search])

  return { data, isLoading }
}

export function useConnectionRelatedMaps(id: string) {
  return useQuery(getConnectionRelatedMapsQuery(id))
}

export function useConnectionRelatedWorkflows(id: string) {
  return useQuery(getConnectionRelatedWorkflowsQuery(id))
}

export function useCartoDW() {
  const { data: connections, isSuccess } = useConnections()
  /**
   * We are calling queries directly because importing useAccount would cause Storybook not to build
   */
  const { queries } = useAccountQueries()
  const { data: accountData } = useQuery(queries.getAccountData)

  const accountHideCartoDatawarehouse = accountData?.hideCartoDatawarehouse
  const settingsHasDWDisabled = !settings.cartoDwEnabled
  const hideCartoDatawarehouse = accountHideCartoDatawarehouse || settingsHasDWDisabled
  return {
    isAvailable: !hideCartoDatawarehouse,
    showConnectionsBanner: hideCartoDatawarehouse && !connections?.length && isSuccess,
    connection: connections?.find((c) => c.carto_dw)
  }
}

interface MetadataParams {
  includeWarehouses: boolean
  includeClusters: boolean
  includeCatalogs: boolean
}

interface UseDatabrickMetadataProps {
  connection?: Connection<DatabricksRestParams> | Connection<DatabricksWarehouseParams>
  form: Omit<UseFormReturn, 'handleSubmit' | 'formState'>
}

export enum MetadataStatus {
  WaitingForCredentials = 'waitingForCredentials',
  Loading = 'loading',
  Error = 'error',
  Empty = 'empty',
  Available = 'available'
}

export function useFormWatchDebounced<T>(
  form: Omit<UseFormReturn, 'handleSubmit' | 'formState'>,
  name: string,
  defaultValue: T,
  delay = 500
): T {
  const formValue = form.watch(name)
  const [debouncedValue, setDebouncedValue] = useState<T>(formValue)
  const isFirstRender = useRef(true)

  const updateValueDebounced = useMemo(
    () =>
      debounce((value: T) => {
        setDebouncedValue(value)
      }, delay),
    [setDebouncedValue, delay]
  )

  useEffect(() => {
    if (isFirstRender.current) {
      if (defaultValue) {
        setDebouncedValue(defaultValue)
      }
      isFirstRender.current = false
    } else {
      updateValueDebounced(formValue)
    }
  }, [formValue, updateValueDebounced, defaultValue, setDebouncedValue])

  return debouncedValue
}

export function useDatabrickMetadata({ form, connection }: UseDatabrickMetadataProps, params: MetadataParams) {
  const connectionType = form.watch('connectionType') as string
  const hostForm = useFormWatchDebounced<string>(form, 'host', connection?.config.credentials.host || '')
  const tokenForm = useFormWatchDebounced<string>(form, 'token', connection?.config.credentials.token || '')
  const servicePrincipalClientIdForm = useFormWatchDebounced<string>(
    form,
    'servicePrincipalClientId',
    connection?.config.credentials.servicePrincipalClientId || ''
  )
  const servicePrincipalSecretForm = useFormWatchDebounced<string>(
    form,
    'servicePrincipalSecret',
    connection?.config.credentials.servicePrincipalSecret || '••••••••'
  )
  const userIdentifierForm = useFormWatchDebounced<string>(
    form,
    'userIdentifier',
    connection?.config.credentials.userIdentifier || ''
  )
  const userEmailForm = useFormWatchDebounced<string>(form, 'userEmail', connection?.config.credentials.userEmail || '')
  const stateForm = useFormWatchDebounced<string>(form, 'state', connection?.config.credentials.state || '')

  const isReady = useMemo(() => {
    if (connection) {
      return true
    } else if (connectionType === 'oauth_u2m') {
      return hostForm && userIdentifierForm && userEmailForm && stateForm
    } else if (connectionType === 'oauth_m2m') {
      return hostForm && servicePrincipalClientIdForm && servicePrincipalSecretForm
    }
    return hostForm && tokenForm
  }, [
    connection,
    connectionType,
    hostForm,
    servicePrincipalClientIdForm,
    servicePrincipalSecretForm,
    stateForm,
    tokenForm,
    userEmailForm,
    userIdentifierForm
  ])

  const isErrorMetadata =
    Boolean(connection?.id) &&
    connectionType === 'oauth_m2m' &&
    (!hostForm || !servicePrincipalClientIdForm || !servicePrincipalSecretForm)

  const getU2MPayload = useCallback(() => {
    return !stateForm && connection
      ? { payload: { ...params, id: connection.id }, isValid: true }
      : {
          payload: {
            ...params,
            host: hostForm,
            userIdentifier: userIdentifierForm,
            userEmail: userEmailForm,
            state: stateForm
          },
          isValid: Boolean(hostForm && userIdentifierForm && userEmailForm && stateForm)
        }
  }, [connection, hostForm, params, stateForm, userEmailForm, userIdentifierForm])

  const getM2MPayload = useCallback(() => {
    return servicePrincipalSecretForm === '••••••••' && connection
      ? { payload: { ...params, id: connection.id }, isValid: true }
      : {
          payload: {
            ...params,
            host: hostForm,
            servicePrincipalClientId: servicePrincipalClientIdForm,
            servicePrincipalSecret: servicePrincipalSecretForm
          },
          isValid: Boolean(hostForm && servicePrincipalClientIdForm && servicePrincipalSecretForm)
        }
  }, [connection, hostForm, params, servicePrincipalClientIdForm, servicePrincipalSecretForm])

  const getTokenPayload = useCallback(() => {
    return !tokenForm && connection
      ? { payload: { ...params, id: connection.id }, isValid: true }
      : {
          payload: {
            ...params,
            host: hostForm,
            token: tokenForm
          },
          isValid: Boolean(hostForm && tokenForm)
        }
  }, [connection, hostForm, params, tokenForm])

  const requiredPayload: { isValid: boolean; payload: DatabricksMetadataPayload } = useMemo(() => {
    if (connectionType === 'oauth_u2m') {
      return getU2MPayload()
    } else if (connectionType === 'oauth_m2m') {
      return getM2MPayload()
    }
    return getTokenPayload()
  }, [connectionType, getM2MPayload, getTokenPayload, getU2MPayload])

  const provider = connection?.provider_id
  const {
    data: _data,
    isLoading,
    isError,
    refetch
  } = useQuery(getDatabricksMetadataQuery({ ...requiredPayload, provider }))

  useEffect(() => {
    if (requiredPayload.isValid) {
      refetch()
    }
  }, [requiredPayload, refetch])

  const clusters = useMemo(
    () =>
      isError || isErrorMetadata
        ? []
        : _data?.clusters?.map((c) => ({ id: c.id, label: `${c.name} (${c.nodeTypeId})` })),
    [_data, isError, isErrorMetadata]
  )
  const warehouses = useMemo(
    () =>
      isError || isErrorMetadata ? [] : _data?.warehouses?.map((c) => ({ id: c.id, label: `${c.name} (${c.size})` })),
    [_data, isError, isErrorMetadata]
  )

  const catalogs = useMemo(
    () => (isError || isErrorMetadata ? [] : _data?.catalogs?.map((c) => ({ id: c.name, label: c.name }))),
    [_data, isError, isErrorMetadata]
  )

  const data = useMemo(() => ({ clusters, warehouses, catalogs }), [clusters, warehouses, catalogs])

  const metadataStatus: MetadataStatus = !isReady
    ? MetadataStatus.WaitingForCredentials
    : isLoading
    ? MetadataStatus.Loading
    : isError || isErrorMetadata
    ? MetadataStatus.Error
    : clusters?.length || warehouses?.length || catalogs?.length
    ? MetadataStatus.Available
    : MetadataStatus.Empty

  return { data, metadataStatus, isValid: requiredPayload.isValid }
}

export function useDatabrickClusters({ form, connection, search }: UseDatabrickMetadataProps & { search: string }) {
  const params = useMemo(() => ({ includeClusters: true, includeWarehouses: false, includeCatalogs: false }), [])
  const { data, metadataStatus, isValid } = useDatabrickMetadata({ form, connection }, params)
  const clusters = useMemo(
    () => data.clusters?.filter((c) => (search ? new RegExp(escapeString(search), 'i').test(c.label) : true)) || [],
    [data, search]
  )
  return { clusters, clustersStatus: metadataStatus, isValid }
}

export function useDatabrickWarehouses({ form, connection, search }: UseDatabrickMetadataProps & { search: string }) {
  const params = useMemo(() => ({ includeClusters: false, includeWarehouses: true, includeCatalogs: false }), [])
  const { data, metadataStatus, isValid } = useDatabrickMetadata({ form, connection }, params)
  const warehouses = useMemo(
    () => data.warehouses?.filter((c) => (search ? new RegExp(escapeString(search), 'i').test(c.label) : true)) || [],
    [data, search]
  )
  return { warehouses, warehousesStatus: metadataStatus, isValid }
}

export function useDatabrickCatalogs({ form, connection, search }: UseDatabrickMetadataProps & { search: string }) {
  const params = useMemo(() => ({ includeClusters: false, includeWarehouses: false, includeCatalogs: true }), [])
  const { data, metadataStatus, isValid } = useDatabrickMetadata({ form, connection }, params)
  const catalogs = useMemo(
    () => data.catalogs?.filter((c) => (search ? new RegExp(escapeString(search), 'i').test(c.label) : true)) || [],
    [data, search]
  )
  return { catalogs, catalogsStatus: metadataStatus, isValid }
}

function escapeString(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
